/* TLC V16 — ready-made e-reader skin pages (template: product.zwapskin-boox).
   Runs after tlc-finish-preview.js has built the finish swatches (.tlc-rfp).
   1. Plain finish swatches: each tile shows the finish on a plain neutral tile
      (same finish engine and preview presets), not on the design. Smaller
      tiles, name always visible under each one.
   2. Phones (<750px): the finish row moves directly under the product photo,
      so the live finish preview and the swatches fit on one screen. The
      thumbnail row becomes dots, and the photo box is trimmed (cover crop,
      82% height) to drop the empty space above and below the mockup.
   3. Device starts as "Select your device". Add to cart / Buy it now are held
      until a device is chosen: the page scrolls to Device and marks it.
   The real Device and Finish dropdowns stay in the form, so variants, price
   and cart work exactly as before. */
(function () {
'use strict';

/* ---------- Finish engine: copied verbatim from tlc-finish-preview.js
   (Tinker Lass Studio's approved Finish tab, CORE-START..CORE-END) ---------- */
/*CORE-START*/
const FX_TAU = Math.PI * 2, FX_RAD = Math.PI / 180;
function fxHash(x, y, s) {
  let h = (Math.imul(x | 0, 374761393) + Math.imul(y | 0, 668265263) + Math.imul(s | 0, 1442695041)) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177); h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}
function fxNoise(x, y, s) {
  const ix = Math.floor(x), iy = Math.floor(y), fx = x - ix, fy = y - iy;
  const a = fxHash(ix, iy, s), b = fxHash(ix + 1, iy, s), c = fxHash(ix, iy + 1, s), d = fxHash(ix + 1, iy + 1, s);
  const ux = fx * fx * (3 - 2 * fx), uy = fy * fy * (3 - 2 * fy);
  return a + (b - a) * ux + (c - a) * uy + (a - b - c + d) * ux * uy;
}
function fxFbm(x, y, s, o) {
  let v = 0, a = .5, t = 0;
  for (let i = 0; i < o; i++) { v += a * fxNoise(x, y, s + i * 17); t += a; x *= 2.03; y *= 2.03; a *= .5; }
  return v / t;
}
function fxWorley(x, y, s) {
  const ix = Math.floor(x), iy = Math.floor(y); let f1 = 9, f2 = 9;
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const cx = ix + dx, cy = iy + dy, px = cx + fxHash(cx, cy, s), py = cy + fxHash(cx, cy, s + 4);
    const d = (px - x) * (px - x) + (py - y) * (py - y);
    if (d < f1) { f2 = f1; f1 = d; } else if (d < f2) f2 = d;
  }
  return Math.sqrt(f2) - Math.sqrt(f1);
}
const fxSmooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };

/* irregular threads running along a, spaced (on average) 1 unit apart in b */
function fxThreads(a, b, seed) {
  const bw = b + .9 * (fxFbm(b * .21, seed * 3.1, seed, 2) - .5) + .3 * (fxNoise(a * .03, b * .08, seed + 9) - .5);
  const idx = Math.floor(bw), d = bw - idx - .5;
  const dd = Math.abs(d - (fxHash(idx, seed, 61) - .5) * .5);
  const th = .03 + .05 * fxHash(idx, seed, 62);
  const prof = Math.exp(-(dd / th) * (dd / th));
  const dash = fxNoise(a * (.05 + .14 * fxHash(idx, seed, 63)) + idx * 13.1, idx * .7, seed + 5);
  return prof * fxSmooth(.32, .55, dash) * (.4 + .6 * fxHash(idx, seed, 64));
}
function fxLeather(u, v) {
  const wx = u + .35 * (fxFbm(u * .3, v * .3, 21, 2) - .5), wy = v + .35 * (fxFbm(u * .3 + 7, v * .3, 22, 2) - .5);
  const c1 = 1 - fxSmooth(0, .09, fxWorley(wx, wy, 3));
  const c2 = 1 - fxSmooth(0, .07, fxWorley(wx * 2.2 + 3.1, wy * 2.2 + 1.7, 11));
  return .9 - Math.max(c1, c2 * .55) + .14 * fxFbm(u * 1.5, v * 1.5, 23, 2);
}
function fxOil(u, v) {
  const th = fxFbm(u * .05, v * .05, 71, 2) * FX_TAU * .7, c = Math.cos(th), s = Math.sin(th);
  return fxFbm((u * c + v * s) * .5, (-u * s + v * c) * 1.4, 73, 3) * .6 + .4 * fxFbm(u * 1.2, v * 1.2, 75, 2);
}
const fxHmCache = new Map();
function fxHeightMap(type, W, H, ppm, size, angle) {
  const key = type + '|' + W + '|' + H + '|' + ppm.toFixed(5) + '|' + size + '|' + angle;
  let h = fxHmCache.get(key); if (h) return h;
  h = new Float32Array(W * H);
  const ca = Math.cos(angle * FX_RAD), sa = Math.sin(angle * FX_RAD), inv = 1 / (ppm * size);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const u = (x * ca + y * sa) * inv, v = (-x * sa + y * ca) * inv; let val;
    switch (type) {
      case 'peel': val = fxFbm(u, v, 51, 2); break;
      case 'frost': val = fxFbm(u, v, 1, 3); break;
      case 'weave': val = Math.max(fxThreads(u, v, 1), fxThreads(v, u, 2)); break;
      case 'leather': val = fxLeather(u, v); break;
      case 'oil': val = fxOil(u, v); break;
      case 'squares': { const fu = u - Math.floor(u) - .5, fv = v - Math.floor(v) - .5; val = 1 - 2 * Math.max(Math.abs(fu), Math.abs(fv)); break; }
      default: val = .5;
    }
    h[y * W + x] = val;
  }
  if (fxHmCache.size > 14) fxHmCache.delete(fxHmCache.keys().next().value);
  fxHmCache.set(key, h);
  return h;
}

function fxSparkle(src, p, L, ppm) {
  if (!(p.density > 0 && p.spBright > 0)) return null;
  const { W, H, data: d } = src;
  const n = Math.min(400000, Math.round((W / ppm) * (H / ppm) / 100 * p.density));
  if (!n) return null;
  const buf = new Float32Array(W * H), rp = p.spSize * ppm * .5, cover = Math.min(1, Math.max(.75, Math.PI * rp * rp));
  const a = L.angle * FX_RAD, cA = Math.cos(a), sA = Math.sin(a), lx = L.px * W, ly = L.py * H, diag = Math.hypot(W, H);
  const add = (x, y, v) => { if (x >= 0 && y >= 0 && x < W && y < H) buf[y * W + x] += v; };
  for (let i = 0; i < n; i++) {
    const fx = fxHash(i, 1, 77) * W, fy = fxHash(i, 2, 77) * H, xi = fx | 0, yi = fy | 0;
    if (d[(yi * W + xi) * 4 + 3] < 8) continue;
    let br = fxHash(i, 3, 77); br = .35 + .65 * br * br;
    const tt = ((fx - lx) * cA + (fy - ly) * sA) / diag;
    let gl = .5 + .5 * Math.cos(fxHash(i, 4, 77) * FX_TAU + tt * 14); gl = gl * gl;
    const v = p.spBright * br * (.75 + .25 * gl);
    if (rp < .8) add(xi, yi, v * cover);
    else { const R = Math.ceil(rp); for (let dy = -R; dy <= R; dy++) for (let dx = -R; dx <= R; dx++) { const q = (dx * dx + dy * dy) / (rp * rp); if (q < 1) add(xi + dx, yi + dy, v * (1 - q)); } }
    if (p.twinkle > 0 && fxHash(i, 5, 77) * 100 < p.twinkle) {
      const len = Math.max(1, Math.round(rp * 7));
      for (let k = 1; k <= len; k++) { const f = v * .85 * Math.pow(1 - k / (len + 1), 2); add(xi + k, yi, f); add(xi - k, yi, f); add(xi, yi + k, f); add(xi, yi - k, f); }
      add(xi, yi, v);
    }
  }
  return buf;
}

const fxEdgeCache = new Map();
function fxEdgeAlpha(src, rad) {
  const key = src.W + 'x' + src.H + ':' + rad; let e = fxEdgeCache.get(key); if (e && e.d === src.data) return e.a;
  const { W, H, data: d } = src; let a = new Float32Array(W * H), t = new Float32Array(W * H);
  for (let i = 0; i < W * H; i++) a[i] = d[i * 4 + 3] / 255;
  const pass = (inp, out, horiz) => { const n = horiz ? W : H, m = horiz ? H : W, win = rad * 2 + 1;
    for (let j = 0; j < m; j++) { let sum = 0; const at = k => { k = k < 0 ? 0 : k >= n ? n - 1 : k; return horiz ? inp[j * W + k] : inp[k * W + j]; };
      for (let k = -rad; k <= rad; k++) sum += at(k);
      for (let k = 0; k < n; k++) { if (horiz) out[j * W + k] = sum / win; else out[k * W + j] = sum / win; sum += at(k + rad + 1) - at(k - rad); } } };
  pass(a, t, true); pass(t, a, false); pass(a, t, true); pass(t, a, false);
  if (fxEdgeCache.size > 6) fxEdgeCache.delete(fxEdgeCache.keys().next().value);
  fxEdgeCache.set(key, { d: src.data, a }); return a;
}
const FX_SILVER = [.14, .02, .14, .02];
/* The print stays as printed; the film only adds reflected light on top. */
function fxRender(src, p, L, ppm) {
  const { W, H, data: d } = src;
  const img = new ImageData(W, H), o = img.data;
  const hm = (p.grain !== 'none' && (p.relief > 0 || p.crease > 0)) ? fxHeightMap(p.grain, W, H, ppm, p.size, p.angle) : null;
  const aa = Math.min(1, (p.size * ppm) / 2.5);
  const k = p.relief * p.size * .25 * ppm * aa;
  const la = L.angle * FX_RAD, el = L.elev * FX_RAD;
  const Lx = Math.cos(la) * Math.cos(el), Ly = Math.sin(la) * Math.cos(el), Lz = Math.sin(el);
  let Hx = Lx, Hy = Ly, Hz = Lz + 1; const hn = Math.hypot(Hx, Hy, Hz); Hx /= hn; Hy /= hn; Hz /= hn;
  const SH = 24, flat = Math.pow(Hz, SH);
  const lx = L.px * W, ly = L.py * H, diag = Math.hypot(W, H), ca = Math.cos(la), sa = Math.sin(la);
  const bw = Math.max(.002, p.boxW * .5), bl = Math.max(.002, p.boxL * .5);
  const sw = Math.max(.003, p.soft * bw * 1.6), sl = Math.max(.003, p.soft * bl * .9);
  const DISP = .03, CURVE = .25;
  const sp = fxSparkle(src, p, L, ppm);
  const erad = Math.max(1, Math.round(.45 * ppm)), ea = p.bevel ? fxEdgeAlpha(src, erad) : null;
  const eWide = p.envDark ? fxEdgeAlpha(src, Math.max(2, Math.round(2.5 * ppm))) : null;
  const holo = p.holo > 0 || p.silver > 0, hc = Math.max(.3, p.size), ha = p.angle * FX_RAD, ca2 = Math.cos(ha), sa2 = Math.sin(ha);
  const { crease, sat, contrast: con, haze, reflect, glow, texMix, glint } = p;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const j = y * W + x, i = j * 4, A = d[i + 3];
      if (!A) continue;
      let r = d[i], g = d[i + 1], b = d[i + 2];
      if (sat !== 1 || con !== 1) {
        const lum = .299 * r + .587 * g + .114 * b;
        r = (lum + (r - lum) * sat - 128) * con + 128; g = (lum + (g - lum) * sat - 128) * con + 128; b = (lum + (b - lum) * sat - 128) * con + 128;
      }
      if (haze) { r += (228 - r) * haze; g += (228 - g) * haze; b += (228 - b) * haze; }
      let nx = 0, ny = 0, nz = 1, micro = 1;
      if (hm) {
        const hl = hm[x > 0 ? j - 1 : j], hr = hm[x < W - 1 ? j + 1 : j], hu = hm[y > 0 ? j - W : j], hd = hm[y < H - 1 ? j + W : j];
        nx = -(hr - hl) * k; ny = -(hd - hu) * k;
        const inv = 1 / Math.sqrt(nx * nx + ny * ny + 1); nx *= inv; ny *= inv; nz = inv;
        if (crease) { const f = 1 - crease * Math.max(0, .9 - hm[j]) * 1.2; r *= f; g *= f; b *= f; }
        micro = Math.min(2.2, Math.pow(Math.max(0, nx * Hx + ny * Hy + nz * Hz), SH) / flat);
        if (p.surface) {
          const sh = (nx * Lx + ny * Ly + nz * Lz - Lz) / Math.max(.15, 1 - Lz) * p.surface * 1.6;
          if (sh > 0) { const q = Math.min(1, sh) * .7; r += q * (255 - r); g += q * (255 - g); b += q * (255 - b); }
        }
      }
      const dx = x - lx, dy = y - ly;
      const t = (dx * ca + dy * sa) / diag, s = (-dx * sa + dy * ca) / diag;
      const tt = t + CURVE * s * s + DISP * (nx * ca + ny * sa), ss = s + DISP * (-nx * sa + ny * ca);
      const at = Math.abs(tt), as = Math.abs(ss);
      let box = 0;
      if (at < bw && as < bl) {
        box = (at <= bw - sw ? 1 : fxSmooth(bw, bw - sw, at)) * (as <= bl - sl ? 1 : fxSmooth(bl, bl - sl, as));
        box *= .82 + .18 * (1 - as / bl);
      }
      if (p.twin) {
        const t2 = Math.abs(tt - bw * 2.6), bw2 = bw * .55, sw2 = sw * .55;
        if (t2 < bw2 && as < bl) box += p.twin * (t2 <= bw2 - sw2 ? 1 : fxSmooth(bw2, bw2 - sw2, t2)) * (as <= bl - sl ? 1 : fxSmooth(bl, bl - sl, as));
      }
      const broad = Math.exp(-(tt * tt + ss * ss) * 5);
      const texed = 1 + (micro - 1) * texMix;
      let R = (reflect * Math.min(1, box) + glow * broad) * texed;
      if (glint && micro > 1) R += glint * (micro - 1) * .35 * (.6 + .4 * broad);
      if (holo) {
        const mx = x / ppm, my = y / ppm, u = (mx * ca2 + my * sa2) / hc, v = (-mx * sa2 + my * ca2) / hc;
        const cu = Math.floor(u), cv = Math.floor(v), fu = u - cu - .5, fv = v - cv - .5;
        const vert = Math.abs(fv) >= Math.abs(fu); // top/bottom triangles form the bright vertical bowtie
        if (p.silver) {
          const lift = p.silver * (vert ? .16 : -.05);
          if (lift > 0) { r += lift * (255 - r); g += lift * (255 - g); b += lift * (255 - b); } else { r += lift * r; g += lift * g; b += lift * b; }
        }
        if (p.holo) {
          const ccu = (cu + .5) * hc, ccv = (cv + .5) * hc;
          const ccx = (ccu * ca2 - ccv * sa2) * ppm, ccy = (ccu * sa2 + ccv * ca2) * ppm;
          const dir = ha + (vert ? Math.PI / 2 : 0) + .5;
          const ph = ((ccx - lx) * Math.cos(dir) + (ccy - ly) * Math.sin(dir)) / diag * 3.5 + fxHash(cu, cv, 44) * .5;
          const along = vert ? Math.abs(fv) * 2 : Math.abs(fu) * 2, across = vert ? Math.abs(fu) / (Math.abs(fv) + 1e-3) : Math.abs(fv) / (Math.abs(fu) + 1e-3);
          let fl = Math.pow(.5 + .5 * Math.cos(FX_TAU * ph), 3) * (vert ? 1 : .45) * (.35 + .65 * fxHash(cu * 2 + (vert ? 1 : 0), cv, 45)) * (1 - .2 * across) * p.holo;
          if (fl > .01) {
            let hh = (ph * .8 + along * .35 + .1 + fxHash(cu, cv, 46) * .7) % 1;   // per-cell colour variety, no single-colour bands if (hh < 0) hh += 1;
            const h6 = hh * 6;
            const cr = Math.min(1, Math.max(0, Math.abs(h6 - 3) - 1)), cg = Math.min(1, Math.max(0, 2 - Math.abs(h6 - 2))), cb = Math.min(1, Math.max(0, 2 - Math.abs(h6 - 4)));
            const k = Math.min(1, fl) * .8;
            r += k * (110 + 145 * cr - r); g += k * (120 + 135 * cg - g); b += k * (130 + 125 * cb - b);
          }
        }
      }
      if (p.env) {
        /* plastic-film reflection: big milky patches along the diagonal, mottled like cling film */
        const es = p.envScale, u = tt * es, v = ss * es;
        const warp = fxFbm(u * 1.5 + 5, v * .8, 97, 3);
        const big = fxSmooth(.47, .7, fxFbm(u * 1.3 + warp * .9, v * .45 + warp * .5, 93, 3));
        if (big > 0) {
          const det = fxFbm(u * 10 + warp * 3, v * 5.5 + warp * 2, 99, 4);
          R += p.env * .72 * big * (.25 + .75 * fxSmooth(.32, .74, det));
        }
        if (p.envDark) {
          /* dark parts of the room reflected in the film: grey smudges that show even on white */
          const dn = fxFbm(u * 1.5 + 17 + warp * .7, v * .6 + warp * .4, 101, 3);
          const ew = eWide ? Math.min(1, Math.max(0, (1 - eWide[j]) * 2.6)) : 0;
          const dk = Math.min(1, fxSmooth(.47, .62, dn) + ew * fxSmooth(.36, .56, dn)) * (1 - big * .8);
          if (dk > 0) {
            const d2 = fxFbm(u * 12 + warp * 3 + 40, v * 6 + warp * 2, 103, 3);
            const f = 1 - p.envDark * .55 * dk * (.3 + .7 * fxSmooth(.3, .75, d2)); r *= f; g *= f; b *= f;
          }
        }
      }
      if (ea) {
        const ab = ea[j];
        if (ab < .995) {
          const gx = ea[x < W - 1 ? j + 1 : j] - ea[x > 0 ? j - 1 : j], gy = ea[y < H - 1 ? j + W : j] - ea[y > 0 ? j - W : j], mg = Math.hypot(gx, gy);
          if (mg > 1e-4) {
            const dd = -(gx * Lx + gy * Ly) / mg / Math.max(.2, Math.hypot(Lx, Ly)), ef = Math.min(1, mg * erad * 1.3) * p.bevel;
            if (dd > 0) R += dd * ef * .75; else { const f = 1 + dd * ef * .3; r *= f; g *= f; b *= f; }
          }
        }
      }
      if (p.shade) { const f = 1 - p.shade * fxSmooth(0, .6, -tt); r *= f; g *= f; b *= f; }
      if (R > 0) { if (R > 1) R = 1; const q = 1 - R; r = 255 - (255 - r) * q; g = 255 - (255 - g) * q; b = 255 - (255 - b) * q; }
      if (sp) { const sv = sp[j]; if (sv > 0) { const q = 1 - (sv > 1 ? 1 : sv); r = 255 - (255 - r) * q; g = 255 - (255 - g) * q; b = 255 - (255 - b) * q; } }
      o[i] = r; o[i + 1] = g; o[i + 2] = b; o[i + 3] = A;
    }
  }
  return img;
}

const FX_BASE = { grain: 'none', size: .5, angle: 0, relief: 0, surface: 0, texMix: .5, glint: 0, crease: 0, contrast: 1, sat: 1, haze: 0, reflect: .3, boxW: .15, boxL: .6, soft: .5, glow: .05, twin: 0, env: 0, envDark: 0, envScale: 3, shade: 0, bevel: 0, density: 0, spSize: .1, spBright: .85, twinkle: 0, holo: 0, silver: 0 };
const FX_DEF = {
  matte:   { bevel: .3, grain: 'frost', size: .2, relief: .5, surface: .15, texMix: .4, reflect: .12, boxW: .4, boxL: 1, soft: 1, glow: .05, contrast: .95, sat: .94, haze: .05 },
  glossy:  { grain: 'none', size: 2, reflect: 0, boxW: .5, boxL: 2, soft: 1, glow: .06, env: .85, envDark: .75, envScale: 2.6, shade: 0, bevel: .7, contrast: 1.05, sat: 1.08 },
  cross:   { bevel: .3, grain: 'weave', size: .8, relief: .7, surface: .45, texMix: .5, glint: .3, reflect: .18, boxW: .3, boxL: 1, soft: .8, glow: .06, sat: 1.02 },
  leather: { bevel: .3, grain: 'leather', size: 1.6, relief: .7, surface: .38, texMix: .5, glint: .2, crease: 0, reflect: .15, boxW: .3, boxL: 1, soft: .85, glow: .05, contrast: .98, haze: .02 },
  square:  { bevel: .3, grain: 'squares', size: 4, relief: 0, surface: 0, texMix: 0, reflect: .25, boxW: .3, boxL: 1.2, soft: .8, glow: .06, holo: 1, silver: .5 },
  shimmer: { bevel: .3, grain: 'frost', size: .15, relief: .15, surface: .05, reflect: .15, boxW: .3, boxL: 1, soft: .8, glow: .05, density: 800, spSize: .16, spBright: 1, twinkle: 1.5 },
  oil:     { bevel: .3, grain: 'oil', size: 1.4, relief: .5, surface: .35, texMix: .5, glint: .15, reflect: .2, boxW: .25, boxL: 1, soft: .7, glow: .06, contrast: 1.04, sat: 1.1 }
};
/*CORE-END*/

/* Preview tuning: same values as tlc-finish-preview.js (keep in step). */
const LIGHT = { px: .32, py: .28, angle: 225, elev: 40 };
const BOOST = { STRENGTH: 1.5, MIN_CELL_PX: 7, MAX_SIZE_X: 1.5 };
const PREVIEW_TUNE = {
  matte:   { boost: false },
  cross:   { boost: false, minCellPx: 4, maxSize: 1.6, set: { size: .8, relief: .95, surface: .5, glint: .3, texMix: .6, crease: .12 } },
  glossy:  { boost: false, set: { env: 0, envDark: 0, twin: 0, reflect: .32, boxW: .75, boxL: 2.4, soft: 1, glow: .18, sat: 1.2, contrast: 1.12, bevel: .8 } },
  leather: { boost: false, minCellPx: 9, maxSize: 4, set: { relief: .8, surface: .38, glint: 0, texMix: .25, crease: .08 } },
  square:  { boost: true }
};
function previewPreset(id, screenPxPerMm) {
  const p = Object.assign({}, FX_BASE, FX_DEF[id]);
  const tune = PREVIEW_TUNE[id] || { boost: false };
  if (tune.set) Object.assign(p, tune.set);
  if (tune.minCellPx && screenPxPerMm > 0) p.size = Math.min(tune.maxSize || p.size, Math.max(p.size, tune.minCellPx / screenPxPerMm));
  if (!tune.boost) return p;
  if (p.grain !== 'none' && p.grain !== 'squares' && BOOST.MIN_CELL_PX > 0 && screenPxPerMm > 0) {
    const need = BOOST.MIN_CELL_PX / screenPxPerMm;
    if (p.size < need) p.size = Math.min(p.size * BOOST.MAX_SIZE_X, need);
  }
  const k = BOOST.STRENGTH;
  p.surface = Math.min(1, p.surface * k);
  p.glint = Math.min(1, p.glint * k);
  p.texMix = Math.min(1, p.texMix * k);
  p.reflect = Math.min(1, p.reflect * (1 + (k - 1) * .6));
  p.env = Math.min(1, p.env * (1 + (k - 1) * .3));
  return p;
}
function finishId(name) {
  const n = String(name || '').toLowerCase();
  if (/gloss/.test(n)) return 'glossy';
  if (/matte/.test(n)) return 'matte';
  if (/leather/.test(n)) return 'leather';
  if (/cross|weave/.test(n)) return 'cross';
  if (/square|holo/.test(n)) return 'square';
  if (/shimmer|flash/.test(n)) return 'shimmer';
  if (/oil/.test(n)) return 'oil';
  return null;
}

/* ---------- 1. plain swatches ---------- */
const SW_PX = 160, SW_MM = 11;            // same tile scale as the design swatches
const SW_TONE = [150, 156, 151];          // plain neutral tile: mid grey-green, shows light and texture
const SW_KEY = 'tlc-v16-sw-1-';
function plainPixels(id) {
  const n = SW_PX * SW_PX, data = new Uint8ClampedArray(n * 4);
  for (let i = 0; i < n; i++) { data[i * 4] = SW_TONE[0]; data[i * 4 + 1] = SW_TONE[1]; data[i * 4 + 2] = SW_TONE[2]; data[i * 4 + 3] = 255; }
  const ppm = SW_PX / SW_MM;
  return fxRender({ W: SW_PX, H: SW_PX, data }, previewPreset(id, ppm), LIGHT, ppm);
}
function plainSwatchUrl(id) {
  try { const c = sessionStorage.getItem(SW_KEY + id); if (c) return c; } catch (e) {}
  const c = document.createElement('canvas'); c.width = c.height = SW_PX;
  c.getContext('2d').putImageData(plainPixels(id), 0, 0);
  const url = c.toDataURL('image/jpeg', 0.9);
  try { sessionStorage.setItem(SW_KEY + id, url); } catch (e) {}
  return url;
}
function paintPlain(wrap) {
  const tiles = Array.from(wrap.querySelectorAll('.tlc-rfp-tile'));
  let i = 0;
  const next = () => {
    const t = tiles[i++]; if (!t) return;
    const id = finishId(t.getAttribute('aria-label') || t.textContent);
    if (id) { try { t.style.setProperty('--tlc-v16-sw', 'url("' + plainSwatchUrl(id) + '")'); } catch (e) {} }
    setTimeout(next, 0);   // one tile per tick so the page stays responsive
  };
  next();
}

/* ---------- 2. phone layout ---------- */
const mq = (typeof window !== 'undefined' && window.matchMedia) ? window.matchMedia('(max-width: 749px)') : { matches: false };
function placeFinish(main, wrap) {
  const fbox = main.querySelector('.tlc-v16-finish-home') || wrap.closest('.product-form__input--dropdown');
  const mediaWrap = main.querySelector('.product__media-wrapper');
  if (!fbox || !mediaWrap) return;
  fbox.classList.add('tlc-v16-finish-home');
  let slot = mediaWrap.querySelector(':scope > .tlc-v16-finish-slot');
  if (!slot) { slot = document.createElement('div'); slot.className = 'tlc-v16-finish-slot'; mediaWrap.appendChild(slot); }
  const place = () => {
    if (mq.matches) { if (wrap.parentElement !== slot) slot.appendChild(wrap); fbox.classList.add('tlc-v16-moved'); }
    else { if (wrap.parentElement !== fbox) fbox.appendChild(wrap); fbox.classList.remove('tlc-v16-moved'); }
  };
  place();
  if (mq.addEventListener) mq.addEventListener('change', place); else if (mq.addListener) mq.addListener(place);
}
function nearestIndex(positions, scrollLeft) {
  let best = 0;
  for (let i = 1; i < positions.length; i++) if (Math.abs(positions[i] - scrollLeft) < Math.abs(positions[best] - scrollLeft)) best = i;
  return best;
}
function setupDots(main) {
  const ul = main.querySelector('.product__media-list');
  if (!ul || ul.dataset.tlcV16Dots === '1') return;
  const items = () => Array.from(ul.children).filter((li) => li.classList.contains('product__media-item') && !li.hidden);
  if (items().length < 2) return;
  ul.dataset.tlcV16Dots = '1';
  const host = ul.closest('slider-component') || ul;
  const bar = document.createElement('div');
  bar.className = 'tlc-v16-dots';
  bar.setAttribute('role', 'group');
  bar.setAttribute('aria-label', 'Product photos');
  host.after(bar);
  let dots = [];
  const positions = () => { const li = items(); const x0 = li[0].offsetLeft; return li.map((e) => e.offsetLeft - x0); };
  function build() {
    const li = items();
    bar.textContent = '';
    dots = li.map((e, i) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'tlc-v16-dot';
      b.setAttribute('aria-label', 'Photo ' + (i + 1) + ' of ' + li.length);
      b.addEventListener('click', () => ul.scrollTo({ left: positions()[i], behavior: 'smooth' }));
      bar.appendChild(b);
      return b;
    });
    update();
  }
  let raf = 0;
  function update() {
    raf = 0;
    const on = nearestIndex(positions(), ul.scrollLeft);
    dots.forEach((d, i) => { d.classList.toggle('is-on', i === on); d.setAttribute('aria-current', i === on ? 'true' : 'false'); });
  }
  ul.addEventListener('scroll', () => { if (!raf) raf = requestAnimationFrame(update); }, { passive: true });
  window.addEventListener('resize', () => { if (!raf) raf = requestAnimationFrame(update); });
  build();
}

/* ---------- 3. device prompt ---------- */
function labelOf(box) { const l = box.querySelector('.form__label, label, legend'); return (l && l.textContent || '').replace(/\s+/g, ' ').trim(); }
function setupDevice(main) {
  const dbox = Array.from(main.querySelectorAll('.product-form__input--dropdown')).find((b) => /device|model/i.test(labelOf(b)));
  const sel = dbox && dbox.querySelector('select');
  if (!sel || sel.dataset.tlcV16 === '1' || sel.options.length < 2) return;
  sel.dataset.tlcV16 = '1';
  // A link with ?variant= means the device was already picked (e.g. after a reload).
  if (!/[?&]variant=/.test(location.search)) {
    const cur = sel.options[sel.selectedIndex] || sel.options[0];
    const ph = cur.cloneNode(false);           // same value, so price, variant and finish changes keep working
    ph.textContent = 'Select your device';
    ph.dataset.tlcV16Ph = '1';
    ph.hidden = true;
    sel.insertBefore(ph, sel.firstChild);
    sel.selectedIndex = 0;
  }
  const unchosen = () => { const o = sel.options[sel.selectedIndex]; return !!(o && o.dataset.tlcV16Ph); };
  let msg = null;
  function clearError() { dbox.classList.remove('tlc-v16-need'); if (msg) { msg.remove(); msg = null; } }
  function demand() {
    dbox.classList.add('tlc-v16-need');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'tlc-v16-need-msg';
      msg.setAttribute('role', 'alert');
      msg.textContent = 'Choose your device first.';
      (sel.closest('.select') || sel).after(msg);
    }
    dbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    try { sel.focus({ preventScroll: true }); } catch (e) {}
  }
  sel.addEventListener('change', () => {
    if (!unchosen()) { const ph = sel.querySelector('[data-tlc-v16-ph]'); if (ph) ph.remove(); clearError(); syncGuards(); }
  });
  // Add to cart: hold the submit until a device is chosen.
  document.addEventListener('submit', (e) => {
    const f = e.target;
    if (!unchosen() || !f || !f.matches || !f.matches('form[action*="/cart/add"]') || !main.contains(f)) return;
    e.preventDefault(); e.stopImmediatePropagation(); demand();
  }, true);
  // Buy it now / Shop Pay: a see-through cover catches the tap while no device is chosen.
  const guards = [];
  main.querySelectorAll('.shopify-payment-button').forEach((pb) => {
    const g = document.createElement('button');
    g.type = 'button'; g.className = 'tlc-v16-pay-guard';
    g.setAttribute('aria-label', 'Choose your device first');
    g.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); demand(); });
    pb.classList.add('tlc-v16-pay-host'); pb.appendChild(g); guards.push(g);
  });
  function syncGuards() { const on = unchosen(); guards.forEach((g) => { g.hidden = !on; }); }
  syncGuards();
}

/* ---------- styles ---------- */
function injectStyles() {
  if (document.getElementById('tlc-v16-skin-mobile')) return;
  const st = document.createElement('style');
  st.id = 'tlc-v16-skin-mobile';
  st.textContent = [
    // smaller plain swatches, name always shown
    '.tlc-rfp .tlc-rfp-grid{display:flex!important;gap:6px!important;max-width:420px!important}',
    '.tlc-rfp .tlc-rfp-tile{flex:1 1 0!important;min-width:0!important;padding:0 0 2px!important;border:0!important;border-radius:0!important;background:none!important;gap:6px!important;font-size:1.2rem!important;line-height:1.2!important;color:#5F665F!important}',
    '.tlc-rfp .tlc-rfp-tile .tlc-fp-sw{width:48px!important;height:48px!important;aspect-ratio:auto!important;border-radius:8px!important;background-color:#969C97!important;background-image:var(--tlc-v16-sw,none)!important;box-shadow:inset 0 0 0 1px rgba(44,38,32,.12)}',
    '.tlc-rfp .tlc-rfp-tile.is-on{font-weight:600!important;color:#2C2620!important}',
    '.tlc-rfp .tlc-rfp-tile.is-on .tlc-fp-sw{box-shadow:0 0 0 2px #FFFFFF,0 0 0 3.5px #4D7050}',
    '.tlc-rfp .tlc-rfp-tile:focus-visible{outline:2px solid #4D7050;outline-offset:3px}',
    // device prompt
    '.tlc-v16-need .select{box-shadow:0 0 0 2px #B42318;border-radius:var(--inputs-radius-outset,var(--inputs-radius,6px))}',
    '.tlc-v16-need-msg{margin:6px 0 0;font-size:1.3rem;line-height:1.4;font-weight:600;color:#B42318}',
    '.tlc-v16-pay-host{position:relative}',
    '.tlc-v16-pay-guard{position:absolute;inset:0;z-index:5;width:100%;height:100%;margin:0;padding:0;border:0;background:transparent;cursor:pointer}',
    '.tlc-v16-pay-guard[hidden]{display:none}',
    '.tlc-v16-finish-slot,.tlc-v16-dots{display:none}',
    '@media(max-width:749px){',
    // finish row under the photo
    '.tlc-v16-moved{display:none!important}',
    '.tlc-v16-finish-slot{display:block;margin:4px 0 0}',
    '.tlc-v16-finish-slot .tlc-rfp{margin:0;gap:8px}',
    '.tlc-v16-finish-slot .tlc-rfp-head{font-size:1.4rem}',
    '.tlc-v16-finish-slot .tlc-rfp-grid{max-width:none!important}',
    '.tlc-v16-finish-slot .tlc-rfp-tile .tlc-fp-sw{width:44px!important;height:44px!important}',
    '.tlc-v16-finish-slot .tlc-rfp-tile{font-size:1.1rem!important}',
    '.tlc-v16-finish-slot .tlc-fp-desc{font-size:1.25rem!important;margin:0}',
    '.tlc-v16-finish-slot .tlc-fp-disclaimer{font-size:1.1rem!important;margin:0}',
    // dots instead of thumbnails
    '.tlc-v16 .product__media-wrapper .thumbnail-slider,.tlc-v16 .product__media-wrapper slider-component:not(.thumbnail-slider) > .slider-buttons{display:none!important}',
    '.tlc-v16-dots{display:flex;justify-content:center;gap:0;margin:2px 0 6px}',
    '.tlc-v16-dot{appearance:none;-webkit-appearance:none;width:24px;height:24px;margin:0;padding:0;border:0;background:none;cursor:pointer;display:grid;place-items:center}',
    '.tlc-v16-dot::before{content:"";width:7px;height:7px;border-radius:50%;background:#CFD5CF;transition:background .15s,transform .15s}',
    '.tlc-v16-dot.is-on::before{background:#4D7050;transform:scale(1.15)}',
    '.tlc-v16-dot:focus-visible{outline:2px solid #4D7050;outline-offset:-2px;border-radius:50%}',
    // trim the empty space around the mockup (cover crop, 82% height)
    '.tlc-v16 .product__media-list .product__media-item .product__media{padding-top:82%!important}',
    '.tlc-v16 .product__media-list .product__media-item .product__media img,.tlc-v16 .product__media-list .product__media-item .tlc-rfp-photo{object-fit:cover!important}',
    // finish badge to the bottom corner, clear of the zoom icon
    '.tlc-v16 .product__media-item .tlc-finish-badge{top:auto!important;bottom:10px!important}',
    '}',
    '@media (prefers-reduced-motion: reduce){.tlc-v16-dot::before{transition:none}}'
  ].join('\n');
  document.head.appendChild(st);
}

/* ---------- boot ---------- */
function boot() {
  const main = document.querySelector('[id^="MainProduct-"]');
  if (!main || document.querySelector('[data-zss-studio]')) return;
  injectStyles();
  main.classList.add('tlc-v16');
  setupDevice(main);
  setupDots(main);
  let tries = 0;
  const wait = () => {
    const wrap = main.querySelector('.tlc-rfp');
    if (wrap) {
      if (wrap.dataset.tlcV16 === '1') return;
      wrap.dataset.tlcV16 = '1';
      placeFinish(main, wrap);
      paintPlain(wrap);
      return;
    }
    if (++tries < 60) setTimeout(wait, 150);   // finish swatches are built by tlc-finish-preview.js
  };
  wait();
}

if (typeof document === 'undefined') {   // Node test hook
  if (typeof module !== 'undefined') module.exports = { plainPixels, previewPreset, finishId, nearestIndex, SW_PX };
  return;
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
document.addEventListener('shopify:section:load', () => setTimeout(boot, 120));
})();
